<template>
    <div class="match-header" :class="{ 'match-header--compact': compact, 'match-header--mobile': $vuetify.breakpoint.xs }">
        <div class="match-header__inner">
            <div v-if="!compact" class="match-header__meta">
                <span v-if="leagueTitle" class="match-chip match-chip--dark">{{ leagueTitle }}</span>
                <span v-if="roundLabel" class="match-chip">{{ roundLabel }}</span>
                <span class="match-chip">{{ phaseLabel }}</span>
                <v-spacer />
                <v-btn v-if="match.pdfUrl" small depressed class="match-header__pdf" @click="$emit('download')">
                    <v-icon small left>mdi-file-download-outline</v-icon>
                    Λήψη PDF
                </v-btn>
            </div>

            <div class="match-header__main">
                <router-link v-if="team1" :to="teamRoute(team1)" class="match-team" :class="teamClass(1)">
                    <span class="match-team__logo">
                        <img v-if="team1.logo" :src="`${assetsUrl}/${team1.logo}`" alt="">
                        <v-icon v-else color="orange">mdi-basketball</v-icon>
                    </span>
                    <span class="match-team__name">{{ team1.name }}</span>
                </router-link>

                <div class="match-center">
                    <div v-if="showScore" class="match-score" :class="{ 'match-score--live': live }">
                        <span :class="scoreClass(1)">{{ score1 }}</span>
                        <span class="match-score__sep">:</span>
                        <span :class="scoreClass(2)">{{ score2 }}</span>
                    </div>
                    <div v-else class="match-kickoff">{{ postponed ? '–:–' : timeLabel }}</div>

                    <span v-if="live" class="match-status match-status--live">
                        <span class="match-status__dot" />
                        LIVE
                    </span>
                    <span v-else-if="postponed" class="match-status match-status--postponed">ΑΝΑΒΟΛΗ</span>
                    <span v-else-if="showScore" class="match-status">Τελικό</span>
                    <span v-else class="match-status match-status--vs">VS</span>
                </div>

                <router-link v-if="team2" :to="teamRoute(team2)" class="match-team match-team--away" :class="teamClass(2)">
                    <span class="match-team__logo">
                        <img v-if="team2.logo" :src="`${assetsUrl}/${team2.logo}`" alt="">
                        <v-icon v-else color="orange">mdi-basketball</v-icon>
                    </span>
                    <span class="match-team__name">{{ team2.name }}</span>
                </router-link>
            </div>

            <div v-if="!compact" class="match-header__info">
                <span v-if="dateLabel" class="match-info">
                    <v-icon small color="orange darken-2">mdi-calendar-outline</v-icon>
                    {{ dateLabel }}
                </span>
                <a
                    v-if="arena"
                    class="match-info match-info--link"
                    :href="arena.coordinates ? `https://www.google.com/maps/search/?api=1&query=${arena.coordinates}` : undefined"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <v-icon small color="orange darken-2">mdi-basketball-hoop-outline</v-icon>
                    {{ arena.name }}
                </a>
            </div>
        </div>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'

    const PHASE_LABELS = {
        regular_season: 'Regular Season',
        placements: 'Προκριματικά',
        playoffs: 'Playoffs'
    }

    // Header of the match page (the older MatchBanner stays as is for the Facebook post images)
    export default {
        props: {
            // stats payload of the match (teams, scores, league, pdf)
            match: {
                type: Object,
                default: () => ({})
            },
            // list entry of the match: pending / postponed / arena / custom round text
            summary: {
                type: Object,
                default: null
            },
            live: {
                type: Boolean,
                default: false
            },
            compact: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            team1() {
                return this.match.team1 || (this.summary && this.summary.team1) || null
            },
            team2() {
                return this.match.team2 || (this.summary && this.summary.team2) || null
            },
            score1() {
                return Number(this.match.team1Score) || 0
            },
            score2() {
                return Number(this.match.team2Score) || 0
            },
            postponed() {
                return !!(this.summary && this.summary.postponed)
            },
            showScore() {
                return this.live || (!this.postponed && (this.score1 > 0 || this.score2 > 0))
            },
            leagueTitle() {
                return this.match.cup ? '' : (this.match.league && this.match.league.title) || ''
            },
            roundLabel() {
                const custom = this.summary && this.summary.customRoundText
                if (custom) return custom
                return this.match.round ? `Αγωνιστική ${this.match.round}` : ''
            },
            phaseLabel() {
                return this.match.cup ? 'Κύπελλο' : (PHASE_LABELS[this.match.phase] || 'Playoffs')
            },
            dateLabel() {
                if (!this.match.matchDatetime) return ''
                return momentTimezone(this.match.matchDatetime).tz('Europe/Athens').locale('el').format('dddd D MMMM YYYY · HH:mm')
            },
            timeLabel() {
                return this.match.matchDatetime ? momentTimezone(this.match.matchDatetime).tz('Europe/Athens').format('HH:mm') : '–:–'
            },
            arena() {
                return (this.summary && this.summary.arena) || null
            }
        },
        methods: {
            teamRoute(team) {
                return team && team.id ? { name: 'Team', params: { id: team.id } } : {}
            },
            winner() {
                if (!this.showScore || this.live || this.score1 === this.score2) return 0
                return this.score1 > this.score2 ? 1 : 2
            },
            scoreClass(team) {
                const winner = this.winner()
                if (!winner) return ''
                return winner === team ? 'is-winner' : 'is-loser'
            },
            teamClass(team) {
                const winner = this.winner()
                return winner && winner !== team ? 'match-team--loser' : ''
            }
        }
    }
</script>

<style scoped>
.match-header {
    padding: 0 12px;
    font-family: Ubuntu, Arial, sans-serif;
}

.match-header__inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 16px 24px 18px;
    border-radius: 14px;
    background: #fff;
    border-top: 4px solid #ff7133;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
    transition: padding 0.25s ease;
}

.match-header__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.match-chip {
    padding: 2px 10px;
    border-radius: 6px;
    background: #f1f1f3;
    color: rgba(0, 0, 0, 0.7);
    font-size: 0.78rem;
    font-weight: 700;
}

.match-chip--dark {
    background: #0d0d0f;
    color: #fff;
}

.match-header__pdf {
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
}

/* ---------- Teams + score ---------- */
.match-header__main {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 20px;
    margin-top: 14px;
}

.match-team {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    gap: 14px;
    min-width: 0;
    color: #2b2b2e !important;
    text-align: right;
    text-decoration: none;
}

.match-team--away {
    flex-direction: row;
    text-align: left;
}

.match-team--loser {
    opacity: 0.55;
}

.match-team__logo {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 96px;
    height: 96px;
    padding: 8px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.match-team__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.match-team__logo .v-icon {
    font-size: 56px !important;
}

.match-team__name {
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.6rem;
    font-weight: 800;
    line-height: 1.15;
    word-break: break-word;
}

.match-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
}

.match-score {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 5px;
    border-radius: 12px;
    background: #0d0d0f;
}

.match-score span {
    min-width: 64px;
    padding: 6px 10px;
    border-radius: 9px;
    color: #fff;
    font-size: 2.4rem;
    font-weight: 800;
    line-height: 1.1;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.match-score span.is-winner {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%);
}

.match-score span.is-loser {
    color: rgba(255, 255, 255, 0.55);
}

.match-score .match-score__sep {
    min-width: 0;
    padding: 0;
    color: rgba(255, 255, 255, 0.4);
}

.match-score--live {
    box-shadow: 0 0 0 2px #ef4444, 0 0 16px rgba(239, 68, 68, 0.5);
}

.match-kickoff {
    padding: 8px 20px;
    border-radius: 12px;
    background: #f1f1f3;
    font-size: 2.2rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
}

.match-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 10px;
    border-radius: 5px;
    background: #f1f1f3;
    color: rgba(0, 0, 0, 0.65);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.match-status--live {
    background: #ef4444;
    color: #fff;
}

.match-status--postponed {
    background: #616161;
    color: #fff;
}

.match-status--vs {
    background: transparent;
    color: rgba(0, 0, 0, 0.4);
    letter-spacing: 0.25em;
}

.match-status__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: match-live 1.2s ease-in-out infinite;
}

@keyframes match-live {
    50% {
        opacity: 0.25;
    }
}

.match-header__info {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px 22px;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.match-info {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: rgba(0, 0, 0, 0.7) !important;
    font-size: 0.9rem;
    font-weight: 600;
    text-decoration: none;
    text-transform: capitalize;
}

.match-info--link:hover {
    color: #ef4444 !important;
}

/* ---------- Compact (sticky while scrolling, desktop) ---------- */
.match-header--compact .match-header__inner {
    padding: 8px 18px;
}

.match-header--compact .match-header__main {
    margin-top: 0;
    gap: 14px;
}

.match-header--compact .match-team__logo {
    width: 48px;
    height: 48px;
    padding: 4px;
}

.match-header--compact .match-team__name {
    font-size: 1.1rem;
}

.match-header--compact .match-score span,
.match-header--compact .match-kickoff {
    min-width: 42px;
    padding: 3px 8px;
    font-size: 1.4rem;
}

.match-header--compact .match-status:not(.match-status--live) {
    display: none;
}

/* ---------- Phone ---------- */
.match-header--mobile .match-header__inner {
    padding: 14px 12px 14px;
}

.match-header--mobile .match-header__main {
    gap: 8px;
}

.match-header--mobile .match-team,
.match-header--mobile .match-team--away {
    flex-direction: column;
    gap: 8px;
    text-align: center;
}

.match-header--mobile .match-team__logo {
    width: 68px;
    height: 68px;
}

.match-header--mobile .match-team__name {
    font-size: 1.05rem;
}

.match-team:hover .match-team__name {
    color: #ef4444;
}

.match-header--mobile .match-score span {
    min-width: 44px;
    padding: 4px 6px;
    font-size: 1.6rem;
}

.match-header--mobile .match-kickoff {
    padding: 6px 12px;
    font-size: 1.5rem;
}

.match-header--mobile .match-header__info {
    flex-direction: column;
    align-items: center;
}

@media (prefers-reduced-motion: reduce) {
    .match-status__dot {
        animation: none;
    }
}
</style>
